<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useModalStore } from '@/store/modal.store';
import ExcelJS from 'exceljs';

const router = useRouter();

const props = defineProps<{
  closeSignal: boolean,
}>();

watch(() => props.closeSignal, () => {
  if (props.closeSignal) {
    close();
  }
});

const exportTypes = new Map<string, string>([
  ['xlsx', 'Microsoft Excel (.XLSX)'],
  ['csv', 'CSV'],
]);

const selectedOption = computed(() => ({
  label: exportTypes.get(selectedFormat.value),
  value: selectedFormat.value
}));

const options = computed(() =>
    Array.from(exportTypes, ([value, label]) => ({ label, value }))
);

const isOpen = ref(false);

function toggleSelect() {
  isOpen.value = !isOpen.value;
}

function selectOption(option: any) {
  selectedFormat.value = option.value;
  setTimeout(() => {
    isOpen.value = false;
  }, 0);
}

function handleClickOutside(event: any) {
  const customSelect = document.querySelector('.custom-select');
  if (!customSelect?.contains(event.target)) {
    isOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});

const headers = computed<string[]>(() => modalStore.modalPayload.underlyingData?.headers || []);
const rowsData = computed<string[][]>(() => modalStore.modalPayload.underlyingData?.rows || []);
const rowLinks = computed<string[]>(() => modalStore.modalPayload.underlyingData?.rowLinks || []);
const headerTitle = computed<string>(() => modalStore.modalPayload.underlyingData?.headerTitle || 'Underlying Data Viewed');

const onRowClick = (index: number) => {
  const link = rowLinks.value[index];
  if (link) {
    modalStore.closeModal();
    router.push(link);
  }
};

const selectedFormat = ref<string>('xlsx');

const tableRef = ref<HTMLElement | null>(null);

const exportTable = async () => {
  if (selectedFormat.value === 'xlsx') {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Data');

    worksheet.addRow(headers.value);

    headers.value.forEach((_, colIndex) => {
      worksheet.getRow(1).getCell(colIndex + 1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'e2ebf4' },
      };
    });

    rowsData.value.forEach((row, index) => {
      const bodyRow = worksheet.addRow(row);

      row.forEach((cellValue, colIndex) => {
        const cell = worksheet.getRow(index + 2).getCell(colIndex + 1);
        if (!isNaN(Number(cellValue)) && cellValue.trim() !== '') {
          cell.value = Number(cellValue);
        } else {
          cell.value = cellValue;
        }

        if (index % 2 !== 0) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'e2ebf4' },
          };
        }
      });

      bodyRow.eachCell((cell: any) => {
        cell.alignment = { horizontal: 'left' };
      });
    });

    worksheet.columns.forEach((column) => {
      if (column.values) {
        let lengths = column.values
          .filter((_, index) => index !== 0)
          .map((val) => {
            return val ? val.toString().length : 10;
          });
        column.width = Math.max(...lengths);
      } else column.width = 25;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    downloadFile(blob, 'UnderlyingDataViewed.xlsx');
  } else if (selectedFormat.value === 'csv') {
    const csvContent = tableToCSVFileContent([headers.value, ...rowsData.value]);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    downloadFile(blob, 'UnderlyingDataViewed.csv');
  }
};

const tableToCSVFileContent = (tableData: string[][]): string => {
  let result = '';

  for (let i = 0; i < tableData.length; i++) {
    const row = tableData[i];
    result += row.map((el) => `${el}`).join(',');
    result += '\n';
  }

  return result;
};

const downloadFile = (blob: Blob | MediaSource, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

const modalStore = useModalStore();
</script>

<template>
	<div class="modal modal--active underlying-data">
    <div class="modal__body underlying">

      <div class="modal__header_underlying">
        <div class="left-part">
          <h3 class="title_underlying">{{ headerTitle }}</h3>
          <p class="date_underlying">{{new Date().toLocaleDateString()}}</p>
        </div>
        <div class="right-part">
          <div class="custom-select">
            <div @click.stop="toggleSelect">
              <span class="material-symbols-outlined expand_more underlying-arrow" :class="{ 'opened': isOpen }">expand_more</span>
              <span class="select-underlying-label">{{ selectedOption.label }}</span>
              <div :class="{ 'dropdown-menu': true, 'show': isOpen }">
                <div v-for="option in options" :key="option.value" @click="selectOption(option)" :class="{ 'dropdown-item': true, 'active': option.value === selectedOption.value }">
                  {{ option.label }}
                </div>
              </div>
            </div>
          </div>

          <button class="exportTableButton" @click="exportTable">
            <svg width="16" height="21" viewBox="0 0 16 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.3077 20.6457C1.80257 20.6457 1.375 20.4707 1.025 20.1207C0.675 19.7707 0.5 19.3432 0.5 18.838V8.45349C0.5 7.94836 0.675 7.52079 1.025 7.17079C1.375 6.82079 1.80257 6.64579 2.3077 6.64579H4.11537C4.32819 6.64579 4.50639 6.71759 4.64997 6.86119C4.79356 7.00477 4.86535 7.18297 4.86535 7.39579C4.86535 7.60861 4.79356 7.78681 4.64997 7.93039C4.50639 8.07398 4.32819 8.14577 4.11537 8.14577H2.3077C2.23077 8.14577 2.16024 8.17782 2.09612 8.24192C2.03202 8.30603 1.99997 8.37656 1.99997 8.45349V18.838C1.99997 18.915 2.03202 18.9855 2.09612 19.0496C2.16024 19.1137 2.23077 19.1458 2.3077 19.1458H13.6922C13.7692 19.1458 13.8397 19.1137 13.9038 19.0496C13.9679 18.9855 14 18.915 14 18.838V8.45349C14 8.37656 13.9679 8.30603 13.9038 8.24192C13.8397 8.17782 13.7692 8.14577 13.6922 8.14577H11.8846C11.6718 8.14577 11.4936 8.07398 11.35 7.93039C11.2064 7.78681 11.1346 7.60861 11.1346 7.39579C11.1346 7.18297 11.2064 7.00477 11.35 6.86119C11.4936 6.71759 11.6718 6.64579 11.8846 6.64579H13.6922C14.1974 6.64579 14.625 6.82079 14.975 7.17079C15.325 7.52079 15.5 7.94836 15.5 8.45349V18.838C15.5 19.3432 15.325 19.7707 14.975 20.1207C14.625 20.4707 14.1974 20.6457 13.6922 20.6457H2.3077V20.6457ZM7.25 2.86502L5.92687 4.18812C5.77817 4.33683 5.60414 4.41023 5.40477 4.40832C5.20542 4.40638 5.02819 4.32786 4.87308 4.17274C4.72819 4.01761 4.65319 3.84197 4.64808 3.64582C4.64294 3.44968 4.71794 3.27405 4.87308 3.11892L7.3673 0.62469C7.54807 0.443907 7.75896 0.353516 7.99997 0.353516C8.24099 0.353516 8.45188 0.443899 8.63265 0.624666L11.1269 3.11889C11.2653 3.25736 11.3362 3.42882 11.3394 3.63329C11.3426 3.83777 11.2718 4.01758 11.1269 4.17272C10.9718 4.32783 10.7936 4.40539 10.5923 4.40539C10.391 4.40539 10.2128 4.32783 10.0577 4.17272L8.74995 2.86499V13.1458C8.74995 13.3586 8.67816 13.5368 8.53458 13.6804C8.39099 13.8239 8.21279 13.8957 7.99997 13.8957C7.78716 13.8957 7.60896 13.8239 7.46537 13.6804C7.32179 13.5368 7.25 13.3586 7.25 13.1458V2.86499V2.86502Z" fill="#0F172A"/>
            </svg>
            <span>Export</span>
          </button>

          <button class="filter-button">
            <svg width="48" height="49" viewBox="0 0 48 49" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect y="0.5" width="48" height="48" rx="8" fill="white"/>
              <path d="M23.0288 30C22.8163 30 22.6382 29.928 22.4945 29.7842C22.3507 29.6404 22.2789 29.4622 22.2789 29.2496C22.2789 29.037 22.3507 28.8589 22.4945 28.7154C22.6382 28.5718 22.8163 28.5 23.0288 28.5H24.9615C25.174 28.5 25.3521 28.5719 25.4959 28.7157C25.6396 28.8595 25.7115 29.0377 25.7115 29.2503C25.7115 29.4629 25.6396 29.641 25.4959 29.7846C25.3521 29.9282 25.174 30 24.9615 30H23.0288ZM19.1538 25.25C18.9413 25.25 18.7632 25.1781 18.6195 25.0343C18.4757 24.8904 18.4039 24.7122 18.4039 24.4997C18.4039 24.2871 18.4757 24.109 18.6195 23.9654C18.7632 23.8218 18.9413 23.75 19.1538 23.75H28.8365C29.049 23.75 29.2271 23.8219 29.3709 23.9657C29.5146 24.1095 29.5865 24.2877 29.5865 24.5003C29.5865 24.7129 29.5146 24.891 29.3709 25.0346C29.2271 25.1782 29.049 25.25 28.8365 25.25H19.1538ZM16.25 20.5C16.0375 20.5 15.8594 20.4281 15.7156 20.2842C15.5719 20.1404 15.5 19.9623 15.5 19.7497C15.5 19.5371 15.5719 19.359 15.7156 19.2154C15.8594 19.0718 16.0375 19 16.25 19H31.75C31.9625 19 32.1406 19.0719 32.2843 19.2157C32.4281 19.3595 32.5 19.5377 32.5 19.7503C32.5 19.9629 32.4281 20.141 32.2843 20.2846C32.1406 20.4282 31.9625 20.5 31.75 20.5H16.25Z" fill="#0F172A"/>
            </svg>
          </button>
					<button @click="modalStore.closeModal" class="modal__close">
						<span class="material-symbols-outlined">close</span>
					</button>
        </div>
      </div>

      <div class="underlying-data-popup">
        <div class="underlying-data-popup__container">
<!--          <div class="underlying-data-popup__header">-->
<!--            <div class="underlying-data-popup__header-start">-->
<!--              <div class="underlying-data-popup__title">Underlying Data Viewed</div>-->
<!--            </div>-->
<!--            <div class="underlying-data-popup__header-end">-->
<!--              <button @click="() => selectedFormat = 'xlsx'">{{ selectedFormat === 'xlsx' && '-&#45;&#45;&#45;&#45;' || '' }} Select xlsx</button>-->
<!--              <button @click="() => selectedFormat = 'csv'">{{ selectedFormat === 'csv' && '-&#45;&#45;&#45;&#45;' || '' }} Select csv</button>-->
<!--               <LightSelect class="underlying-data-popup__select" :options="exportTypes" v-model="selectedFormat" :reverse="true" /> -->
<!--               <SecondarySublink class="underlying-data-popup__export" :sublink="{ label: 'Export', icon: 'ios_share', action: 'export' }" :padding="true" @click="exportTable" /> -->
<!--              <button @click="exportTable">Export</button>-->
<!--            </div>-->
<!--          </div>-->
          <div class="underlying-data-popup__body">
            <table class="underlying-data-popup__table" ref="tableRef">
              <thead class="underlying-data-popup__table-head">
                <tr>
                  <td class="underlying-data-popup__table-cell" v-for="cell in headers" :key="cell">
                    {{ cell }}
                  </td>
                </tr>
              </thead>
              <tbody class="underlying-data-tbody">
                <tr
                  class="underlying-data-popup__table-row"
                  v-for="(row, index) in rowsData"
                  :key="index"
                  :class="{
                    'underlying-data-popup__table-row--colored': index % 2 !== 0,
                    'underlying-data-popup__table-row--clickable': rowLinks[index]
                  }"
                  @click="onRowClick(index)"
                >
                  <td class="underlying-data-popup__table-cell" v-for="cell in row" :key="cell">
                    {{ cell }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>


    </div>
  </div>
</template>


<style lang="scss" scoped>

.modal.underlying-data {
  min-width: 100vw;
  padding-left: 8vw;
}

.modal__body.underlying {
  width: 90%;
  max-width: unset;
  height: 100%;
  max-height: 90vh;
}

.custom-select {
  position: relative;
  display: inline-block;
  cursor: pointer;
}

.material-symbols-outlined {
  vertical-align: middle;
}

.dropdown-menu {
  list-style-type: none;
  margin: 0;
  padding: 0;
  display: none;
  position: absolute;
  top: 40px;
  left: 0;
  background-color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 9;
  width: 100%;
  min-width: 312px;
}

.dropdown-menu.show {
  display: block;
}

.dropdown-item {
  padding: 8px 16px;
  cursor: pointer;
}

.dropdown-item.active {
  background-color: #f0f0f0;
}

.dropdown-item:hover {
  background-color: #e0e0e0;
}

.modal__header_underlying {
  display: grid;
  grid-template-columns: repeat(2, auto);
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 16px 24px;
}

.modal__header_underlying .left-part {
  display: grid;
  gap: 8px;
}

.modal__header_underlying .right-part {
  display: grid;
  grid-template-columns: repeat(4, auto);
  gap: 16px;
  align-items: center;
}

.title_underlying {
  color: #0F172A;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: 125%; /* 27.5px */
}

.date_underlying {
  color: #72777B;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%; /* 25.6px */
}

.underlying-arrow {
  color: #64748B;
}

.select-underlying-label, .dropdown-item {
  color: #72777B;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%; /* 25.6px */
}

.underlying-arrow {
  margin-bottom: 3px;
  margin-right: 8px;
}

.underlying-arrow.opened {
  transform: rotate(180deg);
}

.custom-select {
  min-width: 312px;
  padding: 5px 12px 3px;
  border-radius: 8px;
  border: 1px solid #D0D5DD;
  background: #FFF;
}

.underlying-wrap {
  margin-left: 16px;
}

.underlying-data-popup {
  width: 100%;
  height: 100%;
  overflow: auto;
  display: flex;
  padding: 16px;
  padding-right: 23px;

  &__container {
    width: fit-content;
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  &__header {
    padding: 24px 56px 16px 24px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;
  }

  &__select {
    min-width: 190px;
  }

  &__title {
    font-weight: 700;
    font-size: 22px;
    margin-bottom: 8px;
  }

  &__date {
    font-size: 16px;
  }

  &__header-end {
    display: flex;
    align-items: center;
    gap: 35px;
  }

  &__export {
    margin-top: -8px;
  }

  &__body {
    padding: 0 16px 16px 16px;
    margin-top: 16px;
    flex: 1;
    overflow: auto;
    position: relative;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
  }

  &__table-head {
    position: sticky;
    top: 0;
  }

  &__table-cell {
    font-size: 16px;
    font-weight: 400;
    padding: 8px 5px;
  }
}

.filter-button {
  border: none;
  background: transparent;
  cursor: pointer;
}

.exportTableButton {
  display: grid;
  grid-template-columns: repeat(2, auto);
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
}

.underlying-data-popup__table-head {
  background: #BFDBFE;
}

.underlying-data-popup__table-head td {
  padding-left: 20px;
  padding-right: 20px;
  border-right: 2px solid #DBEAFE;
  border-left: 2px solid #DBEAFE;
  color: #0F172A;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
}

.underlying-data-tbody tr td {
  padding-left: 20px;
  padding-right: 20px;
  border-bottom: .5px solid #64738b;
  border-right: 2px solid #DBEAFE;
  border-left: 2px solid #DBEAFE;

  color: #64748B;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
}

.underlying-data-tbody tr:nth-child(odd) {
  background-color: #eff6ff;
}

.underlying-data-popup__table-row--clickable {
  cursor: pointer;
}

.underlying-data-popup__table-row--clickable:hover {
  background-color: #dbeafe !important;
}

</style>